/* eslint-disable sorare/no-detached-fragments */
import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonProps } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { DEFAULT_SEASON_SLUG } from '@sorare/core/src/constants/routes';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { rivals } from '@sorare/core/src/lib/glossary';

import { UserSeasonEntry } from './UserSeasonEntry';
import {
  PastSeasonsSelectorQuery,
  PastSeasonsSelectorQueryVariables,
  SeasonSelector_footballRivalsSeason,
} from './__generated__/index.graphql';

const DropdownContent = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  min-width: 250px;
  overflow-y: auto;
  background-color: var(--c-nd-100);

  & > :not(:last-child) {
    border-bottom: 1px solid var(--c-nd-150);
  }
`;

const footballRivalsSeasonFragment = gql`
  fragment SeasonSelector_footballRivalsSeason on FootballRivalsSeason {
    id
    slug
    startDate
    cutOffDate
    ranking(userSlug: $userSlug) @include(if: $isUserQuery) {
      id
      points
      ranking
      divisionChange
      leaderboard {
        id
        rankingsCount
      }
      divisionConfig {
        id
        title
        badgeUrl
      }
    }
    ...UserSeasonEntry_footballRivalsSeason @include(if: $isUserQuery)
  }
  ${UserSeasonEntry.fragments.footballRivalsSeason}
` as TypedDocumentNode<SeasonSelector_footballRivalsSeason>;
const SEASON_SELECTOR_QUERY = gql`
  query PastSeasonsSelectorQuery(
    $userSlug: String!
    $after: String
    $isUserQuery: Boolean!
  ) {
    football {
      rivals {
        id
        currentSeason {
          slug
          ...SeasonSelector_footballRivalsSeason
        }
        pastSeasons(after: $after, first: 10) {
          nodes {
            slug
            ...SeasonSelector_footballRivalsSeason
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${footballRivalsSeasonFragment}
` as TypedDocumentNode<
  PastSeasonsSelectorQuery,
  PastSeasonsSelectorQueryVariables
>;

export enum SeasonSelectorType {
  SQUAD,
  USER,
}
type Props = {
  seasonSlug: string;
  userSlug: string;
  type: SeasonSelectorType;
  onChange: (seasonSlug: string) => void;
  fullWidth?: boolean;
  buttonSize?: ComponentProps<typeof FilterInDropdown>['buttonSize'];
  render?: (
    props: Pick<ButtonProps, 'onClick' | 'onMouseEnter' | 'onMouseLeave'> & {
      text?: ReactElement | null;
      season?: PastSeasonsSelectorQuery['football']['rivals']['currentSeason'];
    }
  ) => ReactElement;
};
export const SeasonSelector = ({
  seasonSlug,
  userSlug,
  type,
  onChange,
  fullWidth,
  buttonSize,
  render,
}: Props) => {
  const { data, loadMore } = usePaginatedQuery(SEASON_SELECTOR_QUERY, {
    connection: 'FootballRivalsSeasonConnection',
    variables: {
      userSlug,
      isUserQuery: type === SeasonSelectorType.USER,
    },
  });

  const { currentSeason, pastSeasons } = data?.football.rivals || {};
  const { pageInfo, nodes } = pastSeasons || {};
  const seasons = [currentSeason, ...(nodes || [])].filter(Boolean);

  const { InfiniteScrollLoader } = useInfiniteScroll(() => {
    loadMore(false, {
      after: pageInfo?.endCursor,
    });
  }, !!pageInfo?.hasNextPage);

  const currentSeasonSelected =
    seasonSlug === DEFAULT_SEASON_SLUG || seasonSlug === currentSeason?.slug;
  const selectedSeason = currentSeasonSelected
    ? currentSeason
    : seasons.find(({ slug }) => slug === seasonSlug);

  const Row = UserSeasonEntry;

  const content = ({ closeDropdown }: { closeDropdown: () => void }) => (
    <DropdownContent>
      {seasons.map((season, i) => (
        <Row
          i={i}
          key={season.slug}
          season={season}
          onClick={() => {
            onChange(season.slug);
            closeDropdown();
          }}
          selected={season.slug === seasonSlug}
        />
      ))}
      <InfiniteScrollLoader />
    </DropdownContent>
  );

  const text = currentSeasonSelected ? (
    <LabelL bold>
      <FormattedMessage {...rivals.currentSeason} />
    </LabelL>
  ) : (
    selectedSeason && (
      <DateRange
        startDate={selectedSeason.startDate}
        endDate={selectedSeason.cutOffDate}
      />
    )
  );
  if (render) {
    return (
      <Dropdown
        label={props => render({ ...props, text, season: selectedSeason })}
      >
        {content}
      </Dropdown>
    );
  }

  return (
    <FilterInDropdown
      buttonLabel={text}
      fullWidth={fullWidth}
      buttonSize={buttonSize}
    >
      {content}
    </FilterInDropdown>
  );
};
